// PACKAGES
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
// APP
import { ContentsSort, InvData } from "src/app/dashboard/file-explorer-page/file-explorer-page.types";
import { FileExplorerService } from 'src/app/_services/file-explorer.service';
import { Project } from 'src/types/Project';

// the API sorts contents, by these only
export type SortMethod = 'NAME' | 'SIZE' | 'MODIFY_DATE';
export type SortDirection = 'ASC' | 'DESC';

export type ViewMode = 'TABLE' | 'ICONS';

const SORT_BY: Record<SortMethod, ContentsSort['sortBy']> = { NAME: 'name', SIZE: 'size', MODIFY_DATE: 'modifyDate' };

export interface FileExplorerState {
  activeProject: Project;
  backupData: InvData[];
  activeDirectory: InvData;
  selectedContent: InvData[];
  sortMethod: SortMethod;
  sortDirection: SortDirection;
  viewMode: ViewMode;
  infoPanelContent: InvData['details'] | null;
  expandedNavRowIds: string[];
}

const initialState: FileExplorerState = {
  activeProject: null,
  backupData: [],
  activeDirectory: null,
  selectedContent: [],
  sortMethod: 'NAME',
  sortDirection: 'ASC',
  viewMode: 'TABLE',
  infoPanelContent: null,
  expandedNavRowIds: [],
}

@Injectable({
  providedIn: 'root'
})
export class FileExplorerStateService {
  state = new BehaviorSubject<FileExplorerState>(initialState);

  /** the page request in flight per folder, so a sort change can drop it */
  private pageRequests = new WeakMap<InvData, Subscription>();

  constructor(private fileExplorerService: FileExplorerService) { }

  get explorerState() {
    return this.state.asObservable();
  }

  /** Get current state values */
  getCurrentState() {
    return this.state.getValue();
  }

  /** Set Active Project for file explorer reference */
  setActiveProject(project: Project, backup: InvData[]) {
    this.state.next({
      ...this.state.getValue(),
      activeProject: project,
      backupData: backup,
      activeDirectory: null,
      selectedContent: [],
      infoPanelContent: null,
      expandedNavRowIds: [],
    });
  }

  /** Set backup data */
  setBackupData(backup: InvData[]) {
    this.state.next({
      ...this.state.getValue(),
      backupData: backup,
    })
  }

  /** Set active directory, clear selected content, and load its first page if it has none for the current sort */
  setActiveDirectory(directory: InvData) {
    this.state.next({
      ...this.state.getValue(),
      activeDirectory: directory,
      selectedContent: [],
      infoPanelContent: null,
    });
    this.loadPage(directory);
  }

  /**
   * Load the first page of a folder's (or a backup session's top level) contents when it has none for the
   * current sort, and with `more`, its next page otherwise
   */
  loadPage(directory: InvData, more = false) {
    const { type, id } = directory.details;
    if(type !== 'DIRECTORY' && type !== 'BACKUP') {
      return;
    }

    const { activeProject, sortMethod, sortDirection } = this.getCurrentState();
    const sort: ContentsSort = { sortBy: SORT_BY[sortMethod], sortOrder: sortDirection === 'ASC' ? 'asc' : 'desc' };
    const sameSort = directory.sort?.sortBy === sort.sortBy && directory.sort?.sortOrder === sort.sortOrder;

    // a page for this sort is already on its way
    if(directory.loading && sameSort) {
      return;
    }

    const firstPage = !directory.contentsFetched || !sameSort;
    if(!firstPage && (!more || directory.nextCursor === null)) {
      return;
    }

    // a page for an older sort is dropped
    this.pageRequests.get(directory)?.unsubscribe();
    directory.loading = true;
    directory.error = false;
    directory.sort = sort;
    // so a reload that fails is retried from the first page
    if(firstPage) {
      directory.contentsFetched = false;
    }

    const query = { ...sort, cursor: firstPage ? null : directory.nextCursor };
    const request = type === 'BACKUP'
      ? this.fileExplorerService.getRootFolders(activeProject.id, id, query)
      : this.fileExplorerService.getFolderContents(activeProject.id, id, query);

    this.pageRequests.set(directory, request.subscribe({
      next: page => {
        const contents = page.contents.map(record => new InvData(record, directory));
        directory.contents = firstPage ? contents : [...directory.contents, ...contents];
        // totals only come with the first page
        if(firstPage) {
          directory.total = page.count;
          directory.folderCount = page.folderCount;
        }
        directory.nextCursor = page.nextCursor;
        directory.contentsFetched = true;
        directory.loading = false;
        this.state.next({ ...this.state.getValue() });
      },
      error: () => {
        directory.loading = false;
        directory.error = true;
        this.state.next({ ...this.state.getValue() });
      },
    }));
  }

  /** add or remove folder ID from list of expanded nav row IDs */
  toggleFolderExpansion(folderId: string) {
    const expandedRows = this.state.getValue().expandedNavRowIds;
    const idIndex = expandedRows.indexOf(folderId);

    // if expanded nav rows array has matching id, remove element
    if(idIndex > -1) {
      expandedRows.splice(idIndex, 1);
    }
    // otherwise add element to array
    else {
      expandedRows.push(folderId);
    }

    this.state.next({
      ...this.state.getValue(),
      expandedNavRowIds: expandedRows,
    });
  }

  /** Set details to display in file explorer info panel */
  setInfoPanelContent(details: InvData['details']) {
    this.state.next({
      ...this.state.getValue(),
      infoPanelContent: details,
    });
  }

  /** Set selected content for file explorer state */
  setSelectedContent(selected: InvData[]) {
    this.state.next({
      ...this.state.getValue(),
      selectedContent: selected,
    })
  }

  /** Set the sort; the API sorts, so the open folder reloads from its first page and its selection is cleared */
  setSort(sortMethod: SortMethod, sortDirection: SortDirection) {
    this.state.next({
      ...this.state.getValue(),
      sortMethod,
      sortDirection,
      selectedContent: [],
    });

    const { activeDirectory } = this.getCurrentState();
    if(activeDirectory) {
      this.loadPage(activeDirectory);
    }
  }
}
